import React, { useState } from 'react'

import { useNotify } from 'utils/useNotify'
import I18n from 'i18n'
import { t } from 'i18n'
import clsx from 'clsx'
import { Grid, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Button from 'components/button'
import Loading from 'components/loading'
import { commonStyles, fetchJson, SignUp, useSignUp } from './common'
import { formatCurrency } from 'utils'

const useStyles = makeStyles((theme) => ({
  ...commonStyles(theme),
  fullHeight: {
    height: '100%'
  },
  mainSubtitle: {
    marginTop: theme.spacing(1)
  },
  boldSubtitle: {
    marginTop: theme.spacing(1),
    fontWeight: 'bold'
  },
  icon: {
    borderRadius: theme.shape.borderRadius * 8,
    padding: theme.spacing(2),
    background: theme.palette.common.white,
    boxShadow: '0px 0px 30px 0px rgb(0 0 0 / 10%)',
    width: 60,
    display: 'inline-block',
    textAlign: 'center'
  },
  title: {
    marginLeft: theme.spacing(2)
  },
  price: {
    marginLeft: theme.spacing(2),
    lineHeight: '60px',
    verticalAlign: 'top'
  },
  priceNote: {
    marginLeft: theme.spacing(2)
  },
  description: {
    color: theme.palette.inputs.label.color.light,
    textAlign: 'justify',
    margin: theme.spacing(3, 0)
  },
  loadingText: {
    textAlign: 'center',
    marginTop: theme.spacing(3)
  }
}))

const PlanOption: React.FC<{
  handlePickPlan: () => unknown
  i18nScope: string
  price?: string
  currency?: string
}> = ({ handlePickPlan, i18nScope, price, currency }) => {
  const classes = useStyles()

  return (
    <Grid item xs={12} md={4}>
      <Paper className={clsx(classes.formPaper, classes.fullHeight)} elevation={0}>
        <Grid container direction='column' justifyContent='space-between' alignItems='stretch' className={classes.fullHeight}>
          <Grid item>
            <Typography component='div' variant='h4' className={classes.title}>
              {t('title', { scope: i18nScope })}
            </Typography>
            <Typography component='span' variant='h2' className={classes.price}>
              {price && formatCurrency(parseFloat(price), currency)}
            </Typography>
            <Typography component='p' className={classes.priceNote}>
              {t('price_note', { scope: i18nScope })}
            </Typography>
            <Typography component='p' className={classes.description}>
              {t('description', { scope: i18nScope })}
            </Typography>
          </Grid>
          <Grid item>
            <div className={classes.buttons}>
              <Button variant='contained' color='primary' className={classes.submit} size='large' onClick={handlePickPlan}>
                {t('choose', { scope: i18nScope })}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  )
}

export const PlanForm: React.FC = () => {
  const classes = useStyles()
  const { notifyError } = useNotify()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { signUp, setSignUp } = useSignUp()

  const handlePickPlan = async (subscriptionPlan: string) => {
    try {
      setIsSubmitting(true)
      const freshSignUp: SignUp = await fetchJson<SignUp>(`/${I18n.locale}/complete_sign_up/select_plan`, {
        method: 'PATCH',
        bodyHash: { subscriptionPlan },
        headers: {
          'Content-Type': 'application/json'
        }
      })

      setIsSubmitting(false)
      setSignUp(freshSignUp)
    } catch (error) {
      setIsSubmitting(false)
      let message: string | undefined
      if (error?.isValidationError) {
        const freshSignUp: SignUp = await error?.response.json()
        setSignUp(freshSignUp)
        message = freshSignUp.errors
      } else {
        message = ''
      }
      notifyError({ message })
    }
  }

  return (
    <Grid container spacing={5}>
      <Grid item xs={12}>
        <Typography variant='h3'>{t('sign_up.plan_form.title')}</Typography>
        <Typography component='p' className={classes.mainSubtitle}>
          {t('sign_up.plan_form.subtitle')}
        </Typography>
        <Typography component='p' className={classes.boldSubtitle}>
          {t('sign_up.plan_form.bold_subtitle')}
        </Typography>
      </Grid>
      {isSubmitting ? (
        <Loading />
      ) : (
        <>
          <PlanOption
            handlePickPlan={() => handlePickPlan('daily')}
            i18nScope='sign_up.plan_form.daily'
            price={signUp.pricing?.daily}
            currency={signUp.pricing?.currency}
          />
          <PlanOption
            handlePickPlan={() => handlePickPlan('monthly_long_term')}
            i18nScope='sign_up.plan_form.monthly_long_term'
            price={signUp.pricing?.monthly_long_term}
            currency={signUp.pricing?.currency}
          />
          <PlanOption
            handlePickPlan={() => handlePickPlan('monthly')}
            i18nScope='sign_up.plan_form.monthly'
            price={signUp.pricing?.monthly}
            currency={signUp.pricing?.currency}
          />
        </>
      )}
    </Grid>
  )
}
